import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import userService from "../services/userService";
import { toast } from "react-toastify";

// Async thunks for user details operations
export const getUserDetails = createAsyncThunk(
  "userDetails/getUserDetails",
  async (id, { rejectWithValue }) => {
    try {
      const response = await userService.getUserDetails(id);
      return response.user;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch user details");
    }
  }
);

export const updateUser = createAsyncThunk(
  "userDetails/updateUser",
  async ({ id, userData }, { rejectWithValue }) => {
    try {
      const response = await userService.updateUser(id, userData);
      toast.success("User updated successfully");
      return response.success;
    } catch (error) {
      toast.error(error.message || "Failed to update user");
      return rejectWithValue(error.message || "Failed to update user");
    }
  }
);

export const deleteUser = createAsyncThunk(
  "userDetails/deleteUser",
  async (id, { rejectWithValue }) => {
    try {
      const response = await userService.deleteUser(id);
      toast.success(response.message || "User deleted successfully");
      return response;
    } catch (error) {
      toast.error(error.message || "Failed to delete user");
      return rejectWithValue(error.message || "Failed to delete user");
    }
  }
);

// Initial state
const initialState = {
  user: null,
  loading: false,
  isUpdated: false,
  isDeleted: false,
  error: null,
};

// User Details slice
const userDetailsSlice = createSlice({
  name: "userDetails",
  initialState,
  reducers: {
    clearErrors: (state) => {
      state.error = null;
    },
    resetUpdate: (state) => {
      state.isUpdated = false;
    },
    resetDelete: (state) => {
      state.isDeleted = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get User Details
      .addCase(getUserDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.error = null;
      })
      .addCase(getUserDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update User
      .addCase(updateUser.pending, (state) => {
        state.loading = true;
        state.isUpdated = false;
        state.error = null;
      })
      .addCase(updateUser.fulfilled, (state) => {
        state.loading = false;
        state.isUpdated = true;
        state.error = null;
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.loading = false;
        state.isUpdated = false;
        state.error = action.payload;
      })
      // Delete User
      .addCase(deleteUser.pending, (state) => {
        state.loading = true;
        state.isDeleted = false;
        state.error = null;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.loading = false;
        state.isDeleted = action.payload.success;
        state.error = null;
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.loading = false;
        state.isDeleted = false;
        state.error = action.payload;
      });
  },
});

export const { clearErrors, resetUpdate, resetDelete } = userDetailsSlice.actions;
export default userDetailsSlice.reducer;

